import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Sparkles, Coffee, PawPrint, Anchor, Wifi, Bed,
  Users, Star, MapPin, ArrowRight, Waves, Bike, Truck,
  PartyPopper, ShieldCheck, Heart, Check,
} from "lucide-react";
import { client, BOOKING_URL, NAP } from "../lib/config";
import SEOHead from "../lib/SEOHead";
import HeroCarousel from "../components/HeroCarousel";
import RoomCard from "../components/RoomCard";
import RoomDialog from "../components/RoomDialog";
import AttractionMedia from "../components/AttractionMedia";

// Format a "YYYY-MM-DD" review date as "Month YYYY" (safe for empty/invalid).
function formatReviewDate(d) {
  if (!d) return "";
  const [y, m] = d.split("-");
  const yr = Number(y);
  const mo = Number(m) - 1;
  if (Number.isNaN(yr) || Number.isNaN(mo) || mo < 0 || mo > 11) return "";
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${MONTHS[mo]} ${yr}`;
}

const HERO_SLIDES = [
  { src: "/images/exterior-1.jpg", alt: "Westbridge Inn & Suites exterior in Clinton, MO", eyebrow: "Westbridge Inn & Suites · Clinton, MO" },
  { src: "/images/room-jacuzzi-1.jpg", alt: "Renovated Jacuzzi suite at Westbridge Inn & Suites", eyebrow: "Renovated Jacuzzi suites" },
  { src: "/images/pool-1.jpg", alt: "Seasonal outdoor pool at Westbridge Inn & Suites", eyebrow: "Seasonal outdoor pool" },
  { src: "/images/room-king-1.jpg", alt: "King suite at Westbridge Inn & Suites", eyebrow: "Clean, renovated rooms" },
  { src: "/images/exterior-6.jpg", alt: "Westbridge Inn & Suites property view", eyebrow: "32 rooms · 24-hour front desk" },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "24-hour front desk", sub: "A real person, every hour." },
  { icon: Coffee, label: "Free hot breakfast", sub: "Fresh every morning." },
  { icon: PawPrint, label: "Pet friendly", sub: "2 pets welcome per room." },
  { icon: Anchor, label: "Boat & truck parking", sub: "Boat charging access too." },
  { icon: Sparkles, label: "Renovated rooms", sub: "LED-lit granite baths." },
  { icon: Heart, label: "Kids under 18 stay free", sub: "In existing beds." },
];

const AUDIENCES = [
  { to: "/truman-lake-fishing", title: "Truman Lake Anglers & Boaters", desc: "20 minutes to Truman Dam. Boat & trailer parking, plus boat charging access.", img: "/images/attractions/truman-dam.jpg", icon: Waves },
  { to: "/katy-trail-cyclists", title: "Katy Trail Cyclists", desc: "You're standing at the western terminus. Real showers, real beds, real coffee.", img: "/images/attractions/katy-trail.jpg", icon: Bike },
  { to: "/lake-activities", title: "KC Weekenders", desc: "1h 15min from Kansas City. Lake, live music, and the Clinton Square all within reach.", img: "/images/attractions/henry-county-courthouse.jpg", icon: Star },
  { to: "/extended-stay", title: "Work Crews & Truckers", desc: "Truck & bus parking, dedicated trucker rate, quiet halls, and a hot breakfast that starts early.", img: "/images/exterior-4.jpg", icon: Truck },
  { to: "/pet-friendly", title: "Pet Travelers", desc: "Two pets welcome per room. Green space to walk. Water bowls at the desk on request.", img: "/images/exterior-7.jpg", icon: PawPrint },
  { to: "/reunions", title: "Festival & Reunion Guests", desc: "Family suites for the whole crew, easy highway access, and space to gather.", img: "/images/exterior-3.jpg", icon: PartyPopper },
];

const CITIES = [
  ["Hotels near Truman Lake", "/truman-lake-fishing"],
  ["Katy Trail lodging", "/katy-trail-cyclists"],
  ["Pet friendly hotel Clinton MO", "/pet-friendly"],
  ["Hotel near Warsaw MO (Truman Dam)", "/local-guide#warsaw"],
  ["Lodging in Windsor MO", "/local-guide#windsor"],
  ["Hotel near Calhoun MO", "/local-guide#calhoun"],
  ["Hotel near Deepwater MO", "/local-guide#deepwater"],
  ["Hotel near Montrose MO", "/local-guide#montrose"],
  ["Hotel near Urich MO", "/local-guide#urich"],
  ["Hotel near Lowry City MO", "/local-guide#lowry-city"],
  ["Hotel near Osceola MO", "/local-guide#osceola"],
  ["Hotel near Tightwad MO", "/local-guide#tightwad"],
  ["Boat parking Clinton MO hotel", "/truman-lake-fishing"],
  ["Trucker rate Clinton MO", "/extended-stay"],
  ["Hunting trip lodging Missouri", "/blog/deer-season-lodging-henry-county"],
  ["Family suite Clinton Missouri", "/boaters"],
];

export default function HomePage() {
  const [rooms, setRooms] = useState([]);
  const [reviewsPayload, setReviewsPayload] = useState({ source: "curated", reviews: [] });
  const [openRoom, setOpenRoom] = useState(null);

  useEffect(() => {
    client.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    client.get("/reviews").then((r) => setReviewsPayload(r.data)).catch(() => {});
  }, []);

  const testimonials = reviewsPayload.reviews || [];
  const isLiveReviews = reviewsPayload.source === "google";

  return (
    <>
      <SEOHead
        title="Hotel in Clinton, MO — Renovated Rooms, Boat & Truck Parking, Free Breakfast"
        description="Westbridge Inn & Suites in Clinton, Missouri. 32 renovated rooms, 24-hour desk, free hot breakfast, seasonal pool, pet friendly, boat & truck parking. Great service. Clean rooms."
      />

      <HeroCarousel
        slides={HERO_SLIDES}
        headline={<>A bridge to a <span className="text-[#FF9666]">good night's rest.</span></>}
        subhead={`${NAP.slogan} 32 renovated rooms in Clinton, Missouri — halfway between Kansas City and the fishing at Truman Lake.`}
      >
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="wb-btn-primary text-base"
          data-testid="hero-book-now"
        >
          Book Now <ArrowRight className="h-4 w-4" />
        </a>
        <a
          href={`tel:${NAP.phoneTel}`}
          className="inline-flex items-center gap-2 bg-white/95 hover:bg-white text-[#1A1A1A] font-bold py-3 px-6 rounded-sm"
          style={{ fontFamily: "'Nunito', sans-serif" }}
          data-testid="hero-call"
        >
          <Phone className="h-4 w-4 text-[#B94A15]" /> {NAP.phone}
        </a>
      </HeroCarousel>

      {/* Trust badges */}
      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]" data-testid="trust-badges-section">
        <div className="wb-container py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {TRUST_BADGES.map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex flex-col items-start gap-3" data-testid={`trust-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-sm bg-[#E85D26]/10 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[#B94A15]" />
                </div>
                <div>
                  <div className="text-sm font-bold text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {label}
                  </div>
                  <div className="text-xs text-[#4A4641] mt-1">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand welcome */}
      <section className="bg-[#FDFBF7]" data-testid="brand-welcome-section">
        <div className="wb-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src="/images/lobby-1.jpg"
              alt="Front desk at Westbridge Inn & Suites"
              className="w-full aspect-[4/5] object-cover rounded-sm border border-[#E5DFD3]"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="wb-eyebrow">Welcome to Westbridge</div>
            <div className="wb-divider mt-4" />
            <h2 className="wb-h2 mt-6">A bridge carries you across the long middle of a journey.</h2>
            <p className="wb-body mt-6">
              Westbridge is that bridge — between the road and a good night's rest. We're a 32-room,
              independently-owned inn in Clinton, Missouri, sitting at the crossroads of MO-7, MO-13
              and MO-18. Kansas City is an hour and fifteen minutes north. Truman Lake and the dam at
              Warsaw are twenty minutes south. The Katy Trail's western terminus is a short walk
              away.
            </p>
            <p className="wb-body mt-4">
              We keep the promise simple: <strong className="text-[#1A1A1A]">clean rooms you can inspect</strong>,
              a <strong className="text-[#1A1A1A]">phone that gets answered</strong>, and a stay built
              around the reason you came — fishing, family, festivals, freight, or just the long haul home.
              Thank you for having us be part of your journey.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" className="wb-btn-outline" data-testid="about-link">
                Our story <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/rooms" className="wb-btn-ghost" data-testid="see-rooms-link">
                See the rooms <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]" data-testid="audiences-section">
        <div className="wb-container py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="wb-eyebrow">Built around your reason for coming</div>
              <h2 className="wb-h2 mt-4 max-w-2xl">
                Who's staying at Westbridge this week?
              </h2>
            </div>
            <p className="wb-body max-w-md">
              Clinton sits at the meeting point of the lake, the trail, and the highway. Here's how we
              set you up for the trip you're actually on.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AUDIENCES.map(({ to, title, desc, img, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`audience-card-${to.slice(1)}`}
                className="group relative overflow-hidden aspect-[4/5] bg-[#1A1A1A]"
              >
                {img ? (
                  <img
                    src={img}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-[opacity,transform] duration-700"
                    loading="lazy"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-[#B94A15]/70 via-[#1A1A1A] to-[#1A1A1A]" aria-hidden="true" />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 opacity-40 group-hover:opacity-55 transition-opacity duration-700"
                      style={{
                        backgroundImage:
                          "radial-gradient(60% 55% at 22% 30%, rgba(232,93,38,0.55) 0%, rgba(232,93,38,0) 60%), radial-gradient(50% 45% at 82% 78%, rgba(245,185,141,0.35) 0%, rgba(245,185,141,0) 60%)",
                      }}
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 opacity-[0.07]"
                      style={{
                        backgroundImage:
                          "linear-gradient(to right, #FDFBF7 1px, transparent 1px), linear-gradient(to bottom, #FDFBF7 1px, transparent 1px)",
                        backgroundSize: "36px 36px",
                      }}
                    />
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" aria-hidden="true" />
                <div className="relative h-full p-6 flex flex-col justify-end text-white">
                  <div className="w-10 h-10 rounded-sm bg-[#E85D26] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-black" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {title}
                  </h3>
                  <p className="mt-2 text-sm text-white/90">{desc}</p>
                  <div className="mt-4 flex items-center gap-2 text-sm font-bold text-[#FF9666] group-hover:gap-3 transition-[gap]">
                    Details <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms preview */}
      <section className="bg-[#FDFBF7]" data-testid="rooms-preview-section">
        <div className="wb-container py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="wb-eyebrow">The rooms</div>
              <h2 className="wb-h2 mt-4 max-w-2xl">
                Six ways to spend the night. All of them freshly cleaned.
              </h2>
            </div>
            <Link to="/rooms" className="wb-btn-outline" data-testid="see-all-rooms">
              See all rooms <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.slice(0, 6).map((room) => (
              <RoomCard key={room.slug} room={room} onOpen={setOpenRoom} />
            ))}
          </div>
        </div>
      </section>

      {/* Jacuzzi conversion */}
      <section className="bg-[#1A1A1A] text-white overflow-hidden" data-testid="jacuzzi-celebrate-section">
        <div className="wb-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="wb-eyebrow text-[#FF9666]">Celebrate</div>
            <div className="wb-divider mt-4" />
            <h2 className="wb-h2 text-white mt-6">
              Anniversary? Birthday? Just made it through the week?
            </h2>
            <p className="wb-body text-white/85 mt-6">
              Our Jacuzzi Suite is our flagship room and our favorite excuse to slow down. A king bed,
              a big private jetted tub, and a room warm enough to actually unwind in. Book it for the
              date night that got rescheduled twice. Or don't tell them and just show up with a bottle.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="wb-btn-primary"
                data-testid="jacuzzi-book-now"
              >
                Book the Jacuzzi Suite <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`tel:${NAP.phoneTel}`}
                className="wb-btn-ghost text-white hover:text-[#FF9666]"
                data-testid="jacuzzi-call"
              >
                <Phone className="h-4 w-4" /> Talk to the desk
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <img src="/images/room-jacuzzi-1.jpg" alt="Jacuzzi suite view" className="w-full aspect-square object-cover" />
            <img src="/images/room-jacuzzi-2.jpg" alt="Jacuzzi suite bathroom" className="w-full aspect-square object-cover mt-8" />
            <img src="/images/room-jacuzzi-3.jpg" alt="Jacuzzi suite room" className="w-full aspect-square object-cover" />
            <img src="/images/room-jacuzzi-classic.jpg" alt="Jacuzzi suite tub detail" className="w-full aspect-square object-cover mt-8" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#FDFBF7]" data-testid="testimonials-section">
        <div className="wb-container py-20 md:py-28">
          <div className="max-w-2xl mb-12 flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="wb-eyebrow">What guests actually say</div>
              <h2 className="wb-h2 mt-4">
                Real feedback from real folks who slept here.
              </h2>
            </div>
            {isLiveReviews && reviewsPayload.rating != null && (
              <div className="text-right text-sm text-[#1A1A1A]" data-testid="home-reviews-aggregate">
                <div className="flex items-center gap-1 justify-end">
                  <Star className="h-4 w-4 fill-[#E85D26] text-[#B94A15]" />
                  <span className="font-black text-lg" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {Number(reviewsPayload.rating).toFixed(1)}
                  </span>
                  <span className="text-[#4A4641]">/ 5 · {reviewsPayload.review_count} reviews</span>
                </div>
                <div className="text-xs text-[#4A4641] mt-1">via Google</div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t, idx) => (
              <figure
                key={t.id || idx}
                className="wb-card p-6 flex flex-col"
                data-testid={`testimonial-${idx}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5" aria-label={`${t.rating || 5} out of 5 stars`}>
                    {Array.from({ length: t.rating || 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-[#E85D26] text-[#B94A15]" aria-hidden="true" />
                    ))}
                  </div>
                  {!isLiveReviews && t.verified && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-[#B94A15] bg-[#FCEEEA] border border-[#F5B98D] px-2 py-1 rounded-sm"
                      style={{ fontFamily: "'Nunito', sans-serif" }}
                      data-testid={`testimonial-verified-${idx}`}
                    >
                      <Check className="h-3 w-3" aria-hidden="true" /> Verified
                    </span>
                  )}
                </div>
                <blockquote className="text-[#1A1A1A] flex-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <span className="text-3xl text-[#B94A15] font-black leading-none" aria-hidden="true">"</span>
                  {isLiveReviews ? t.text : t.quote}
                </blockquote>
                <figcaption className="mt-6 pt-4 border-t border-[#E5DFD3]">
                  <div className="font-bold text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {isLiveReviews ? t.author : t.name}
                  </div>
                  <div className="text-xs text-[#4A4641] mt-0.5">
                    {isLiveReviews ? (
                      <>{t.relative_time || ""} · <span>via Google</span></>
                    ) : (
                      <>
                        {formatReviewDate(t.review_date)}
                        {t.review_date && t.source_platform ? " · " : ""}
                        {t.source_platform && (
                          <span>
                            via <span className="font-bold text-[#1A1A1A]">{t.source_platform}</span>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Local guide teaser */}
      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]" data-testid="local-guide-teaser">
        <div className="wb-container py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="wb-eyebrow">The neighborhood</div>
              <h2 className="wb-h2 mt-4 max-w-2xl">
                We know Clinton. Let us point you at the good parts.
              </h2>
            </div>
            <Link to="/local-guide" className="wb-btn-outline" data-testid="open-local-guide">
              Open the local guide <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Truman Lake & Truman Dam", category: "Lake · 38 min south", desc: "20 minutes to Warsaw. World-class crappie fishing, plus boat ramps at Sterett Creek and Osage Bluff.", image: "/images/attractions/truman-dam.jpg", credit: "USACE (public domain)" },
              { title: "The Clinton Square", category: "Downtown · 3 min", desc: "One of the largest courthouse squares in Missouri. Coffee, antiques, and dinner within a short drive.", image: "/images/attractions/henry-county-courthouse.jpg", credit: "Kbh3rd (CC BY-SA 4.0)" },
              { title: "Katy Trail — Western Terminus", category: "Trail · 6 min", desc: "The 240-mile trail starts in Clinton. Real coffee, real showers, and secure bike storage on request.", image: "/images/attractions/katy-trail.jpg", credit: "via Wikimedia Commons" },
            ].map((c) => (
              <Link
                key={c.title}
                to="/local-guide"
                className="group block"
                data-testid={`local-teaser-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <h3 className="wb-h3 mt-4">{c.title}</h3>
                <p className="wb-body mt-2 text-base">{c.desc}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#4A4641] mt-2">Photo: {c.credit}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internal-link SEO grid */}
      <section className="bg-[#FDFBF7]" data-testid="seo-grid-section">
        <div className="wb-container py-20 md:py-28">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">Nearby & related</div>
            <h2 className="wb-h2 mt-4">
              Coming from a small town? We're probably the closest real hotel.
            </h2>
            <p className="wb-body mt-4">
              Clinton sits at the intersection of a handful of Henry County communities. Whichever
              direction you're coming from, we've got you a clean bed.
            </p>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {CITIES.map(([label, to]) => (
              <li key={label}>
                <Link
                  to={to}
                  data-testid={`seo-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="block px-4 py-3 bg-[#F5F0E6] hover:bg-[#FCEEEA] border border-[#E5DFD3] text-sm text-[#1A1A1A] hover:text-[#B94A15] font-medium rounded-sm"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#E85D26] text-white" data-testid="final-cta-section">
        <div className="wb-container py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-sm font-black uppercase tracking-[0.18em] text-white/80" style={{ fontFamily: "'Nunito', sans-serif" }}>
              Ready when you are
            </div>
            <h2 className="wb-h2 text-white mt-3 max-w-xl">
              Book direct. Talk to a real person. Sleep well.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#B94A15] font-bold py-3 px-6 rounded-sm hover:bg-[#FDFBF7]"
              style={{ fontFamily: "'Nunito', sans-serif" }}
              data-testid="final-book-now"
            >
              Book Now <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`tel:${NAP.phoneTel}`}
              className="inline-flex items-center gap-2 border-2 border-white text-white font-bold py-3 px-6 rounded-sm hover:bg-white hover:text-[#B94A15]"
              style={{ fontFamily: "'Nunito', sans-serif" }}
              data-testid="final-call"
            >
              <Phone className="h-4 w-4" /> {NAP.phone}
            </a>
          </div>
        </div>
      </section>

      <RoomDialog room={openRoom} open={!!openRoom} onOpenChange={(v) => !v && setOpenRoom(null)} />
    </>
  );
}
